import { createFileRoute, useRouter } from "@tanstack/react-router";
import visualImgSrc from "$/login/visual_customer.svg";
import visualBusinessImgSrc from "$/login/visual_business.svg";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  // useRef
} from "react";
import { AppType, BusinessType, SignType } from "#/unique/domain";
import { useStorage } from "^/common/useStorage";
import { usePreload } from "^/common/usePreload";
import { useAppColor } from "^/common/useAppColor";
// import { useLoadingStore } from "&/loading";
// import { AppleLoginButton } from "../components/AppleLoginButton";
import { SHA256 } from "crypto-js";
import { useCookies } from "react-cookie";
import { unifiedLogin } from "../utils/unifiedLogin";
import { useLoginHandlers } from "../utils/loginHandlers";
import { AnimatePresence, motion } from "framer-motion";
import { ThemedArrowButton } from "@/Button";
// import { publicHttp } from "~/config/axios";

export const Route = createFileRoute("/login-stage")({
  component: LoginStage,
});
// const sti = setTimeout(() => null, 0);
function LoginStage() {
  const { history } = useRouter();
  // const count = useRef(0);
  // const { toggleLoading } = useLoadingStore();
  const [type] = useStorage<AppType>("session-app-type");
  const [, setCookie] = useCookies(["email"]);
  const [sns, setSns] = useStorage<SignType>("local-sign-sns-type");
  const [fcm, setFcm] = useStorage<string>("session-fcm");
  const [, setBusiness] = useStorage<BusinessType>("local-business-type");
  const [, setType] = useStorage<AppType>("session-app-type");
  const params = new URL(document.URL).searchParams;
  const snsType = params.get("sns-type");
  const hash = params.get("hash");
  const [, setIsReady] = useState(true);
  const { handleLoginSuccess, handleLoginFailure } = useLoginHandlers();
  const userAgent = navigator.userAgent;
  const isAndroid = useMemo(() => {
    return /MT_A/i.test(userAgent);
  }, [userAgent]);
  const isIOS = useMemo(() => {
    return /MT_I/i.test(userAgent);
  }, [userAgent]);

  // 자동로그인 처리
  const handleAutoLogin = useCallback(async () => {
    if (!sns || !hash) {
      console.warn("자동로그인에 필요한 데이터가 없습니다:", { sns, hash });
      return;
    }

    try {
      const result = await unifiedLogin({
        userType: import.meta.env.VITE_APP_TYPE as AppType,
        snsType: sns,
        hash,
        fcmToken: fcm,
        cdMobileType: isIOS ? "IOS" : "ANDROID",
      });

      if (result.success) {
        await handleLoginSuccess(
          import.meta.env.VITE_APP_TYPE as AppType,
          result.data
        );
      } else {
        console.error("자동로그인 실패:", result.error);
        handleLoginFailure(
          import.meta.env.VITE_APP_TYPE as AppType,
          result.statusCode || 500,
          sns
        );
      }
    } catch (error) {
      console.error("자동로그인 처리 중 오류:", error);
      setIsReady(true);
    }
  }, [
    sns,
    hash,
    fcm,
    isIOS,
    handleLoginSuccess,
    handleLoginFailure,
    setIsReady,
  ]);

  useEffect(() => {
    if (hash && sns) {
      handleAutoLogin();
    }
    setType(import.meta.env.VITE_APP_TYPE);
    // URL에 sns-type 파라미터가 있을 때만 설정
    if (snsType) {
      setSns(snsType as SignType);
    }
    if (!hash) {
      setIsReady(true);
    }
  }, [hash, sns, fcm, snsType, setType, setIsReady, handleAutoLogin]);

  // SNS 로그인 처리
  const handleSnsLogin = useCallback(
    async (data: any) => {
      try {
        console.log("SNS 로그인 시작:", data);

        const loginData = JSON.parse(data).payload;
        const loginFcm = JSON.parse(data)["fcm-token"];

        if (!loginData?.snsId || !loginData?.cdSnsType) {
          console.error("SNS 로그인 데이터가 올바르지 않습니다:", loginData);
          return;
        }

        console.log("SNS 로그인 데이터:", { loginData, loginFcm });

        // 해시 생성 (앱 연동은 로그인 성공 후에 수행)
        const key = loginData.cdSnsType + loginData.snsId;
        const hash = SHA256(key).toString();

        // 이메일 쿠키 설정
        if (loginData.email) {
          setCookie("email", loginData.email, {
            secure: true,
            maxAge: 3600,
          });
        }

        console.log("통합 로그인 호출 시작...");

        // 통합 로그인 함수 사용
        const result = await unifiedLogin({
          userType: import.meta.env.VITE_APP_TYPE as AppType,
          snsType: loginData.cdSnsType,
          hash,
          fcmToken: loginFcm,
          cdMobileType: isIOS ? "IOS" : "ANDROID",
          email: loginData.email,
        });

        console.log("통합 로그인 결과:", result);

        if (result.success) {
          console.log("로그인 성공, 메인 페이지로 이동");
          // ⚠️ 로그인 성공 시에만 appLogin 호출
          if (isAndroid) {
            window.mung.appLogin(hash);
          }
          if (isIOS) {
            const array = ["appLogin", hash];
            window.webkit?.messageHandlers?.mung?.postMessage(array);
          }

          await handleLoginSuccess(
            import.meta.env.VITE_APP_TYPE as AppType,
            result.data
          );
        } else {
          console.log("로그인 실패, 상태 코드:", result.statusCode);
          console.log("로그인 실패, 에러:", result.error);

          // 회원가입이 필요한 경우 (201) 또는 기타 실패
          handleLoginFailure(
            import.meta.env.VITE_APP_TYPE as AppType,
            result.statusCode || 500,
            loginData.cdSnsType
          );
        }
      } catch (error) {
        console.error("SNS 로그인 처리 중 오류:", error);
        alert(
          "로그인 처리 중 오류가 발생했습니다: " +
            (error instanceof Error ? error.message : "알 수 없는 오류")
        );
      }
    },
    [isIOS, handleLoginSuccess, handleLoginFailure, setCookie]
  );

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isApp = /MT_I|MT_A/i.test(userAgent);
    if (isApp) {
      // window.fnHistoryBack은 __root.tsx에서 전역적으로 처리됨
      // window.receiveDeepLink = (path: string, query: string) => {
      //   alert(path + query);
      // };
      window.callAppLogin = (data: string) => {
        setFcm(data);
      };
      window.onNativeLogin = (data: any) => {
        handleSnsLogin(data);
      };
    }
  }, [handleSnsLogin]);

  usePreload(visualImgSrc, visualBusinessImgSrc);
  useAppColor({
    background: "#ff9934",
  });
  const visualImg = useMemo(() => {
    switch (type) {
      default:
      case "customer":
        return visualImgSrc;
      case "business":
        return visualBusinessImgSrc;
    }
  }, [type]);
  // useEffect(() => {
  //   return () => {
  //     toggleLoading(false);
  //   };
  // }, [toggleLoading]);
  const handleGoSignIn = useCallback(
    (business: BusinessType) => {
      if (business) {
        setBusiness(business);
        history.push(`/sign-up/${business}`);
      }
    },
    [history, sns]
  );
  return (
    <div className="flex flex-col min-h-screen bg-yellow">
      <div className="flex-auto content-center m-auto w-fit">
        <img
          src={visualImg}
          alt="멍투데이"
          className="object-contain p-3 max-w-full max-h-96"
        />
      </div>
      <div className="px-5 py-10 text-center bg-white rounded-t-3xl drop-shadow-2xl flex-0">
        <h1 className="mb-10 text-2xl font-medium">시작하기</h1>
        <div className="flex flex-col gap-4">
          <AnimatePresence>
            <>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ThemedArrowButton
                  color="secondary"
                  size="xl"
                  className="m-auto w-64 rounded-full stroke-yellow"
                  onClick={() => handleGoSignIn("business-owner")}
                >
                  원장님 회원가입
                </ThemedArrowButton>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ThemedArrowButton
                  color="secondary"
                  size="xl"
                  className="m-auto w-64 rounded-full stroke-[#7D6DFF] border-[#7D6DFF] text-[#7D6DFF]"
                  onClick={() => handleGoSignIn("business-teacher")}
                >
                  선생님 회원가입
                </ThemedArrowButton>
              </motion.div>
            </>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
